import cn from 'clsx'
import useTranslation from 'next-translate/useTranslation'
import { CartItemsList } from '@components/cart/CartItemsList'
import { FiatAndCryptoPrice } from '@components/web3/FiatAndCryptoPrice'
import { ChevronDown, Cross } from '@components/icons'
import { DataResponse, isError, isSuccess } from '@lib/api'
import { formatFiatPrice } from '@lib/web3/helpers/formatFiatPrice'
import { OrderLine } from '@lib/api'
import { SupportedCoins } from '@lib/web3/types/SupportedCoins'
import { computeFiatToCoinConversionWithRate } from '@lib/web3/helpers/computeFiatToCoinConversionWithRate'
import { NFTInformation } from '../NFTInformation'

interface OrderSummaryProps {
  cartData: DataResponse<{
    subTotal: number
    lines: OrderLine[]
    customFields?: { nftState?: string | null } | null
  } | null>
  taxes?: number
  className?: string
  selectedCoin: SupportedCoins | null
  conversionRate: number | null
  isShown: boolean
  setIsShown: (isShown: boolean) => void
}

export const OrderSummary = ({
  cartData,
  taxes,
  className,
  selectedCoin,
  conversionRate,
  isShown,
  setIsShown,
}: OrderSummaryProps) => {
  const { t } = useTranslation()

  const lines = isSuccess(cartData) && cartData.data ? cartData.data.lines : []

  return (
    <>
      {isError(cartData) ? (
        <div
          className={cn(
            'flex-1 px-4 flex flex-col justify-center items-center',
            className
          )}
        >
          <span className="border border-white rounded-full flex items-center justify-center w-16 h-16">
            <Cross width={24} height={24} />
          </span>
          <h2 className="pt-6 text-xl font-light text-center">
            {t('cart:cart_retrieval_error')}
          </h2>
        </div>
      ) : isSuccess(cartData) && cartData.data && lines.length ? (
        <div className={cn('lg:bg-black/20 lg:px-3 lg:min-h-full', className)}>
          <div
            className={cn('mb-4 border-deepPurple lg:border-b-0 lg:hidden', {
              'border-b': isShown,
            })}
          />
          <div
            className={cn('flex flex-col lg:hidden', {
              hidden: isShown,
            })}
          >
            <div
              id="mobile-top-part"
              className="flex items-center mx-content-margin-mobile sm:mx-content-margin-desktop"
            >
              <p className="text-xl font-bold mr-2">
                {t('checkout:total')}:{' '}
                {formatFiatPrice(cartData.data.subTotal + (taxes ?? 0))}
              </p>

              <button
                className="ml-auto flex items-center uppercase"
                onClick={() => setIsShown(true)}
              >
                {t('checkout:order_summary')}
                <ChevronDown />
              </button>
            </div>
          </div>
          <div
            className={cn('my-4 border-b border-deepPurple lg:hidden', {
              hidden: isShown,
            })}
          />
          <div
            id="order-summary-transition-child"
            className={cn(
              'lg:max-h-full overflow-hidden transition-all lg:scale-y-100 lg:h-fit',
              {
                'scale-y-0 h-0': !isShown,
              }
            )}
          >
            <div
              className={cn(
                'mt-5 hidden lg:static border-b border-deepPurple',
                {
                  'scale-y-0': !isShown,
                }
              )}
            />
            <div className="flex items-center justify-between mb-3 mt-3 mx-content-margin-mobile sm:mx-content-margin-desktop lg:mb-5 lg:mt-6">
              <p className="text-4xl font-archivoBlack text-blue">
                {t('checkout:order_summary')}
              </p>
              <button
                onClick={() => setIsShown(false)}
                aria-label="Close order summary"
                className="hover:text-accent-5 transition ease-in-out duration-150 flex items-center focus:outline-none lg:hidden"
              >
                <Cross className="h-9 w-9 hover:text-accent-3" />
              </button>
            </div>
            <div className="mx-content-margin-mobile sm:mx-content-margin-desktop">
              <CartItemsList
                lines={lines}
                editable={false}
                selectedCoin={selectedCoin}
                conversionRate={conversionRate}
              />
              {isSuccess(cartData) &&
              cartData.data.customFields?.nftState === 'Allocated' ? (
                <NFTInformation
                  title={t('cart:nft_airdrop')}
                  description={t('cart:nft_airdrop_description')}
                  className="mb-8 mt-10"
                />
              ) : null}
              <div className="my-4 border-b border-deepPurple" />
              <div className="flex flex-col items-end mb-10">
                <div className="w-full flex items-start justify-between">
                  <p className="text-xl font-bold mr-8">
                    {t('cart:sub_total')}
                  </p>
                  <FiatAndCryptoPrice
                    coin={selectedCoin}
                    fiatPrice={cartData.data.subTotal}
                    cryptoPrice={computeFiatToCoinConversionWithRate(
                      cartData.data.subTotal,
                      conversionRate
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col items-end mb-10">
                <div className="w-full flex items-start justify-between">
                  <p className="font-bold mr-8">{t('checkout:taxes')}</p>
                  {taxes !== undefined ? (
                    <FiatAndCryptoPrice
                      coin={selectedCoin}
                      fiatPrice={taxes}
                      cryptoPrice={computeFiatToCoinConversionWithRate(
                        taxes,
                        conversionRate
                      )}
                    />
                  ) : (
                    t('checkout:taxes_tbc')
                  )}
                </div>
              </div>
              <p className="w-full flex justify-between">
                <span className="font-bold">{t('checkout:shipping')}</span>
                <span>{t('checkout:shipping_free')}</span>
              </p>
              <div className="mt-8 mb-4 border-b border-deepPurple" />
              <div className="flex flex-col items-end mb-10">
                <div className="w-full flex items-start justify-between">
                  <p className="text-4xl font-bold mr-8">
                    {t('checkout:total')}
                  </p>
                  <FiatAndCryptoPrice
                    coin={selectedCoin}
                    fiatPrice={cartData.data.subTotal + (taxes ?? 0)}
                    cryptoPrice={computeFiatToCoinConversionWithRate(
                      cartData.data.subTotal + (taxes ?? 0),
                      conversionRate
                    )}
                    size="bigger"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
